import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useT } from '../services/i18n';
import { subcategoryPath, nodeNames } from '../services/catalogPath';

// Level-2 page: brand × sub-sub-category cross filters — one click opens the child page with the brand pre-filtered
const QuickCrossFilters = ({ category, parent, children, products, subsOf, labelOf }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const rows = useMemo(() => children.map((child) => {
    const names = nodeNames(category, child);
    const counts = {};
    products.filter(p => subsOf(p).some(s => names.includes(s))).forEach(p => { if (p.brand) counts[p.brand] = (counts[p.brand] || 0) + 1; });
    return { child, brands: Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 8), total: Object.values(counts).reduce((a, b) => a + b, 0) };
  }).filter(r => r.total > 0), [category, children, products, subsOf]);
  if (!rows.length) return null;

  const go = (child, brand) => {
    const key = `catFilters:${category.slug}/${child}`;
    if (brand) localStorage.setItem(key, JSON.stringify({ brand: [brand], price: { min: null, max: null }, priceInput: { min: '', max: '' }, attr: {} }));
    else localStorage.removeItem(key);
    navigate(subcategoryPath(category, child));
  };

  return (
    <section className="bg-white rounded-xl shadow-sm p-5 mb-6" data-testid="quick-cross-filters">
      <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 mb-1"><Zap size={16} className="text-orange-500" /> {t('cat.quickFilters')}</h2>
      <p className="text-xs text-gray-500 mb-3">{t('cat.quickFiltersHint', { name: labelOf(parent) })}</p>
      <div className="divide-y divide-gray-100">
        {rows.map(({ child, brands, total }) => (
          <div key={child} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`quick-row-${child}`}>
            <button type="button" onClick={() => go(child)} className="text-sm font-medium text-gray-800 hover:text-orange-600 text-left sm:w-56 flex-shrink-0" data-testid={`quick-child-${child}`}>
              {labelOf(child)} <span className="text-gray-400 font-normal">({total})</span>
            </button>
            <div className="flex flex-wrap gap-1.5">
              {brands.map(([brand, n]) => (
                <button key={brand} type="button" onClick={() => go(child, brand)} className="text-xs border border-gray-200 bg-gray-50 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 rounded-full px-2.5 py-1 transition-colors" data-testid={`quick-brand-${child}-${brand}`}>
                  {brand} <span className="text-gray-400">{n}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default QuickCrossFilters;
